import React, { useState } from 'react';
import { TrendingUp, TrendingDown, AlertCircle, Award } from 'lucide-react';

export default function SupplierAccountability() {
  const suppliers = [
    {
      id: 1,
      name: 'Supplier A',
      status: 'excellent',
      onTime: 98,
      shortShip: 0.5,
      confirmLatency: '0.6h',
      orderVolume: 156,
      trend: '+2%',
      orders: 40,
      score: 98,
    },
    {
      id: 2,
      name: 'Supplier B',
      status: 'good',
      onTime: 92,
      shortShip: 2.1,
      confirmLatency: '1.2h',
      orderVolume: 89,
      trend: '-1%',
      orders: 25,
      score: 92,
    },
    {
      id: 3,
      name: 'Supplier C (Trending Up)',
      status: 'improving',
      onTime: 87,
      shortShip: 3.5,
      confirmLatency: '2.1h',
      orderVolume: 45,
      trend: '+5%',
      orders: 15,
      score: 87,
    },
    {
      id: 4,
      name: 'Supplier D (Trending Down)',
      status: 'declining',
      onTime: 78,
      shortShip: 5.2,
      confirmLatency: '3.4h',
      orderVolume: 32,
      trend: '-5%',
      orders: 10,
      score: 78,
    },
  ];

  const tiers = [
    { tier: 'A-Tier (Reliable)', count: 1, orderPercent: 40, score: '95%+' },
    { tier: 'B-Tier (Good)', count: 1, orderPercent: 25, score: '85-94%' },
    { tier: 'C-Tier (Monitor)', count: 1, orderPercent: 15, score: '75-84%' },
    { tier: 'D-Tier (Review)', count: 1, orderPercent: 20, score: '<75%' },
  ];

  const StatusBadge = ({ status }) => {
    const styles = {
      excellent: 'bg-green-500/20 text-green-400 border border-green-500/30',
      good: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
      improving: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
      declining: 'bg-red-500/20 text-red-400 border border-red-500/30',
    };

    const labels = {
      excellent: '✓ Excellent',
      good: '✓ Good',
      improving: '↗ Improving',
      declining: '↘ Declining',
    };

    return <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status]}`}>{labels[status]}</span>;
  };

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Supplier Accountability</h2>
        <p className="text-white/60">Supplier performance rankings and accountability scores</p>
      </div>

      {/* Supplier Tiers */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <Award size={20} className="text-yellow-400" />
          Order Volume by Supplier Tier
        </h3>
        <div className="space-y-4">
          {tiers.map((tier) => (
            <div key={tier.tier}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-white text-sm font-medium">{tier.tier}</p>
                <div className="flex items-center gap-4">
                  <p className="text-white/60 text-xs">{tier.count} suppliers</p>
                  <p className="text-white font-semibold">{tier.orderPercent}% of orders</p>
                </div>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    tier.orderPercent >= 35
                      ? 'bg-green-400'
                      : tier.orderPercent >= 15
                        ? 'bg-blue-400'
                        : tier.orderPercent >= 10
                          ? 'bg-yellow-400'
                          : 'bg-red-400'
                  }`}
                  style={{ width: `${tier.orderPercent}%` }}
                />
              </div>
              <p className="text-white/40 text-xs mt-1">Score threshold: {tier.score}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Supplier Rankings */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white">Supplier Rankings</h3>
        {suppliers.map((supplier) => (
          <div key={supplier.id} className="bg-white/5 border border-white/10 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold text-white">{supplier.name}</h4>
                <StatusBadge status={supplier.status} />
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-white">{supplier.score}%</p>
                <p className="text-white/50 text-xs">Reliability Score</p>
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-white/60 text-xs mb-1">On-Time Rate</p>
                <p className="text-white font-semibold">{supplier.onTime}%</p>
              </div>
              <div>
                <p className="text-white/60 text-xs mb-1">Short-Ship Rate</p>
                <p className="text-white font-semibold">{supplier.shortShip}%</p>
              </div>
              <div>
                <p className="text-white/60 text-xs mb-1">Confirm Latency</p>
                <p className="text-white font-semibold">{supplier.confirmLatency}</p>
              </div>
              <div>
                <p className="text-white/60 text-xs mb-1">Orders This Month</p>
                <p className="text-white font-semibold">{supplier.orders}</p>
              </div>
            </div>

            {/* Trend */}
            <div className="flex items-center gap-2 pt-4 border-t border-white/5">
              {supplier.trend.startsWith('+') ? (
                <TrendingUp size={16} className="text-green-400" />
              ) : (
                <TrendingDown size={16} className="text-red-400" />
              )}
              <p className={supplier.trend.startsWith('+') ? 'text-green-400 text-sm' : 'text-red-400 text-sm'}>
                {supplier.trend} vs last week
              </p>
              <div className="flex-1" />
              <p className="text-white/50 text-xs">{(supplier.orders / 156) * 100 | 0}% of order volume</p>
            </div>
          </div>
        ))}
      </div>

      {/* Recommendation */}
      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <AlertCircle size={20} className="text-yellow-400" />
          Recommendation
        </h3>
        <div className="space-y-2 text-sm">
          <p className="text-white/80">
            <span className="text-yellow-400 font-semibold">Supplier D (Trending Down)</span> is declining
            (78% reliability, -5% trend). Consider:
          </p>
          <ul className="list-disc list-inside text-white/70 space-y-1">
            <li>Reduce order volume from 10 → 5 orders/month</li>
            <li>Monitor next 2 weeks closely</li>
            <li>Prepare backup supplier from B-tier</li>
            <li>Schedule performance review call</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
